﻿import LocationHitMaps from "./locationHitMaps";

export const searchPage = () => {
  /** Wires up event to post form whenever values (e.g. filters) change. */
  const wireUpFormInputElements = () => {
    const form = document.getElementById("search-page-form") as HTMLFormElement;

    form
      .querySelectorAll<HTMLInputElement>("[data-search-type=keywords]")
      .forEach(elm => {
        elm.addEventListener("change", _ => {
          if (elm.disabled) {
            // Ignore changes to disabled elements
            return;
          }

          // clear any section filters and refresh the form
          clearAllFilters();
        });
      });

    form.querySelectorAll<HTMLInputElement>("input[type=checkbox]").forEach(elm => {
      elm.addEventListener("change", _ => {
        if (elm.disabled) {
          // Ignore changes to disabled elements
          return;
        }

        // clear other checkboxes
        const form = elm.closest("form") as HTMLFormElement;
        form
          .querySelectorAll<HTMLInputElement>(`input[type=checkbox][name=${elm.name}]`)
          .forEach(checkbox => {
            if (checkbox !== elm && checkbox.checked) {
              checkbox.checked = false;
            }
          });

        refreshForm(form);
      });
    });

    // don't click through to filter panel
    form.querySelectorAll<HTMLInputElement>("label").forEach(elm => {
      elm.addEventListener("click", e => e.stopPropagation());
    });

    form
      .querySelectorAll<HTMLElement>("[data-js-search-page-all-results")
      .forEach(elm => {
        elm.addEventListener("click", e => {
          e.preventDefault();
          clearAllFilters();
        });
      });

    // click on desktop filters sets (mobile) checkboxes
    form.querySelectorAll<HTMLElement>("[data-js-search-page-filter").forEach(elm => {
      elm.addEventListener("click", e => {
        const val = elm.dataset.jsSearchPageFilter;
        if (!val) {
          console.warn(
            "Filter button is missing value data attribute. Reverting to default click behavior.",
            elm
          );
          return;
        }
        let valueSet = false;
        // set corresponding checkbox
        (elm.closest("form") as HTMLFormElement)
          ?.querySelectorAll<HTMLInputElement>(`input[type=checkbox][value="${val}"]`)
          .forEach(checkbox => {
            valueSet = true;
            checkbox.checked = true;
            checkbox.dispatchEvent(new Event("change"));
          });

        if (valueSet) {
          e.preventDefault();
        } else {
          console.warn(`Filter checkbox missing for value "{val}"`);
        }
      });
    });
  };

  /** Posts a form asynchronously and returns the response HTML. */
  const postForm = (form: HTMLFormElement) =>
    new Promise<string>((resolve, reject) => {
      fetch(form.action, {
        method: "POST",
        body: new FormData(form),
      })
        .then(response => response.text())
        .then(html => {
          resolve(html);
        })
        .catch(error => {
          reject(error);
        });
    });

  /** Updates query string to match current filters AFTER loading the search results */
  const updateUrl = () => {
    const params = new URLSearchParams(window.location.search);

    // Update query string to match current filters
    const searchWrapper = document.getElementById("search-wrapper") as HTMLElement;

    const filters = searchWrapper.dataset["filters"] || "";
    const query = searchWrapper.dataset["query"] || "";
    const page = searchWrapper.dataset["page"] || "";

    let hasParameters = false;

    const setQueryString = (name: string, value: string | null) => {
      if (value) {
        // We don't use encodeURIComponent primarily for aesthetic reasons,
        // instead we explicitly replace expected characters
        value = value.replace("&", "%26");

        params.set(name, value);
        hasParameters = true;
      } else {
        params.delete(name);
      }
    };

    setQueryString("q", query);
    setQueryString("section", filters);
    setQueryString("page", page);

    const queryString = hasParameters ? `?${decodeURIComponent(params.toString())}` : "";

    const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${queryString}`;

    window.history.replaceState({}, "", newUrl);
  };

  /** Posts the current form and expects HTML to be returned for updating the page. */
  const refreshForm = (form: HTMLFormElement) => {
    postForm(form).then(
      async html => {
        const container = document.getElementById("search-wrapper") as HTMLElement;

        container.outerHTML = html;

        updateUrl();

        initialize();

        // Location hit maps should only be initialized on AJAX requests and Usercentrics events.
        await LocationHitMaps.initialize();
      },
      error => {
        console.error("Failed to refresh search form", error);
      }
    );
  };

  /** Sets the visibility of the filters panel (applicable for tablet and mobile). */
  const showHideFilterPanel = (show: boolean | null) => {
    const filtersContainer = document.getElementById("filters") as HTMLDivElement;

    if (show === null) {
      show = !filtersContainer.classList.contains("!translate-x-0");
    }

    const overlay = document.getElementById("search-wrapper-overlay") as HTMLDivElement;

    const filtersExpanded = document.getElementById(
      "filtersExpanded"
    ) as HTMLInputElement;

    // Set checkbox to indicate whether panel is open
    filtersExpanded.checked = show;

    // Toggle visibility of filters container
    if (show) {
      filtersContainer.classList.add("!translate-x-0");

      overlay.classList.remove("pointer-events-none");
      overlay.classList.add("!opacity-100");
    } else {
      filtersContainer.classList.remove("!translate-x-0");

      overlay.classList.add("pointer-events-none");
      overlay.classList.remove("!opacity-100");
    }
  };

  /** Shows/hides the filters panel (in mobile and tablet) when button is clicked. */
  const wireUpFiltersButton = () => {
    const filtersButton = document.getElementById(
      "show-hide-filters-button"
    ) as HTMLButtonElement;

    filtersButton.addEventListener("click", _ => {
      showHideFilterPanel(null);
    });
  };

  /** Resets all filters */
  const clearAllFilters = () => {
    const form = document.getElementById("search-page-form") as HTMLFormElement;

    form.querySelectorAll<HTMLInputElement>("input[type=checkbox]").forEach(elm => {
      elm.checked = false;
    });

    refreshForm(form);
  };

  /** Configures search textbox */
  const configureSearchBoxes = () => {
    const searchTextboxes = document.querySelectorAll<HTMLInputElement>(
      "[data-search-type=keywords]"
    );

    searchTextboxes.forEach(element => {
      // Trigger change on keywords textbox when Enter is pressed
      element.addEventListener("keydown", event => {
        if (event.key === "Enter") {
          (event.target as HTMLInputElement).blur(); // To trigger change, if we trigger the actual 'change' event we get double posts
        }
      });

      // Reset search box value when X is clicked
      (
        element.parentElement?.querySelector(
          "svg[data-search-function=clear]"
        ) as HTMLElement
      ).addEventListener("click", event => {
        element.value = "";

        // Return to search icon
        const clearIcon = event.target as HTMLElement;

        clearIcon.classList.add("hidden");

        const searchIcon = element.parentElement?.querySelector(
          "svg:not([data-search-function=clear])"
        );

        searchIcon?.classList.remove("hidden");

        // Make the change take effect
        element.parentElement?.querySelector("input")?.dispatchEvent(new Event("change"));
      });
    });
  };

  const configureForm = () => {
    // Prevent POST of form, as we'll do it async (AJAX)
    const form = document.getElementById("search-page-form") as HTMLFormElement;

    form.onsubmit = event => {
      event.preventDefault();
    };

    // Prevent form from being submitted by hitting Enter
    form.addEventListener("keydown", event => {
      if (event.key === "Enter") {
        event.preventDefault();
      }
    });
  };

  const setupPaging = () => {
    const pageInput = document.querySelector("#search-page-form input[name=page]") as
      | HTMLInputElement
      | undefined;

    if (!pageInput) {
      console.error("Missing paging input in form");
      return;
    }

    // make sure the input is clear initially
    pageInput.value = "0";

    const handlePageLinkClick = (e: MouseEvent) => {
      const pageLink = e.currentTarget as HTMLElement | undefined;
      const pageNumber = pageLink?.dataset.pageNumber;
      if (!pageNumber) {
        console.warn("Page link without page number clicked");
        return;
      }

      e.preventDefault();
      pageInput.value = pageNumber;

      const scrollToElement = document.querySelector(
        "[data-js-search-page-scroll-anchor]"
      ) as HTMLElement | undefined;
      if (scrollToElement) {
        scrollToElement.scrollIntoView({ behavior: "smooth" });
      }

      refreshForm(pageLink.closest("form") as HTMLFormElement);
    };

    document
      .querySelectorAll("[data-js-search-page-paging] [data-page-number]")
      .forEach(pageLink => {
        (pageLink as HTMLElement).addEventListener("click", handlePageLinkClick);
      });
  };

  /** Sets up behaviors after page load/refresh. */
  const initialize = () => {
    configureSearchBoxes();

    wireUpFormInputElements();

    wireUpFiltersButton();

    configureForm();

    setupPaging();

    // Hide filters panel on Esc (applicable for tablet and mobile)
    document.addEventListener("keydown", event => {
      if (event.key === "Escape") {
        showHideFilterPanel(false);
      }
    });

    // Hide filters panel if clicking outside (i.e. on the overlay)
    document.getElementById("search-wrapper-overlay")?.addEventListener("click", _ => {
      showHideFilterPanel(false);
    });

    // Hide filters panel if "Show results" button is clicked
    (
      document.getElementById("search-page-filters-panel-button") as HTMLButtonElement
    ).addEventListener("click", _ => {
      showHideFilterPanel(false);
    });

    // Clear filters on click
    (
      document.getElementById("search-page-clear-filters-button") as HTMLElement
    ).addEventListener("click", clearAllFilters);
  };

  // Initial location map initialization should only be done on Usercentrics event to
  // avoid flashing cookie consent warning text.
  LocationHitMaps.setupUsercentricsEventHandlers();

  initialize();
};
